/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, {useEffect, useRef, useState} from 'react';
import {PropTypes} from 'prop-types';
import styled, {css} from 'styled-components';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import IMask from 'imask';

import Spinner, {SpinnerSize} from '../Spinner';
import {FiraSansBold, FontWeight} from '../Text';
import Button from '../ButtonForCalendar';
import {Breakpoints} from "../../constants/Breakpoints";
import {mediaHelper} from "../../utils/src";
import { handleScrollWhenInputIsFocused } from '@fitinsur/utils/src/scroll-input-focus';

export const InputSize = {
  SMALL: 1,
  NORMAL: 2
};

const propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  mask: PropTypes.any,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  validation: PropTypes.bool,
  valueCalendar: PropTypes.any,
  originalSize: PropTypes.bool,
  customValue: PropTypes.string,
};

const defaultProps = {
  type: 'text',
  mask: null,
  placeholder: '',
  leftIcon: null,
  rightIcon: null,
  leftRender: null,
  rightRender: null,
  label: null,
  format: value => value,
  parse: value => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: value => value,
  onBlur: value => value,
  onFocus: value => value,
  onPressEnter: value => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  validation: true,
  valueCalendar: null,
  originalSize: false,
  customValue: null,
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};

  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

const inverseBlueInputMixin = css`
  background-color: transparent;
  color: ${Colors.WHITE};

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
  &:active:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
`;

const inverseBlueContainerMixin = css`
  background: none;
  border-color: ${Colors.WHITE};

  & input {
    ${inverseBlueInputMixin}

    &[disabled] {
      background: none;
    }
  }
`;

const smallInputMixin = css`
  height: 32px;
`;

// Components

export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  margin-bottom: 10px;

  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};

  transition: box-shadow 0.15s ease;
  
  ${({ isFocused }) =>
    isFocused &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}

  ${({ inline }) =>
    inline &&
    css`
      min-width: 115px;
      width: 80%;
    `}
  
  ${({ meta }) => (meta && meta.touched && meta.error ? errorInputMixin : '')}

  ${({ inverseBlue }) => inverseBlue && inverseBlueContainerMixin}
  
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
  p{
    width: 34px;
    height: 25px;
    font-size: 14px;
    padding-top: 3px;
    padding-left: 2px;
    color: #666666;
    font-family: 'FiraSans',sans-serif;
    font-weight: bold;
    font-style: normal;
   
  }
`;

export const Input = styled.input`
  width: ${({ smallInput }) => (smallInput ? '40px' : '100%')};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;  
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  transition: background 0.15s ease, box-shadow 0.15s ease;
  
  height: 42px;
  min-height: unset;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }

  &::placeholder {
    color: ${Colors.GRAY[3]};
  }

  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
  
  ${({originalSize}) => (originalSize === true ? 'height: 42px;': '')}

  ${({ size }) => size && _getInputSizeMixin(size)}
`;

export const IconButton = styled(Button).attrs({
  type: 'button',
  tabIndex: -1
})`
  padding: 0;
  height: 24px;
  
  ${mediaHelper.sm`
    height: unset;
  `}

  & ${Icon} {
    font-size: 14px;
    color: ${Colors.GRAY[3]};
    width: 30px;
    height: 30px;
    background-size: 100%;
    margin: 3px;
    
    ${mediaHelper.sm`
      width: 30px;
      height: 30px;
      margin: 0 12px;
    `}
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  ${({ inline }) =>
    inline &&
    css`
      display: flex;
    `}

  ${({ disabled, inverse }) =>
    disabled &&
    inverse &&
    css`
      opacity: 0.3;
    `}
`;

const _getInputSizeMixin = size => {
  if (size === InputSize.SMALL) {
    return smallInputMixin;
  }

  return '';
};



const InputField = ({
  type,
  placeholder,
  disabled,
  readOnly,
  mask,
  maskChar,
  leftIcon,
  leftIconClick,
  leftIconText,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  classname,
  form: { setFieldValue, setFieldTouched, touched, errors },
  inputProps,
  validationMinHeight,
  hideLabelAsterisk,
  smallInput,
  alignCenter,
  inline,
  validation,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  valueCalendar,
  onSearchSelect,
  searchList,
  originalSize,
  customValue,
  isCalendar,
  ...props
}) => {
  const inputRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  

  useEffect(() => {
    if (
      inputRef &&
      inputRef.current &&
      inputRef.current.selectionStart &&
      (selectionStart.current || selectionStart.current === 0)
    ) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;
  
  const metaFields = field.name.split('.');
  if (metaFields.length == 3) {
    touchedList = touched[metaFields[0]]
    if (touchedList) {
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if (errorList) {
      errorItem = errorList[parseInt(metaFields[1])]
    }
  }

  const meta = {
    touched: metaFields.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field.name] : touched[field.name],
    error: metaFields.length == 3 && errorItem ? errorItem[metaFields[2]] || errors[field.name] : errors[field.name],
    isFocused
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  useEffect(() => {

  }, [])

  const maskInstance = mask ? IMask.createMask(mask) : null;
  const _onBlur = () => {
    setIsFocused(false);
  };


  useEffect(() => {
    try {
      if (maskInstance) {
        if (mask.type == "currency" || mask.type == 'percentage') {
          if ('blocks' in mask) {
            let opt = mask;
            opt.blocks.num.padFractionalZeros = true;
            maskInstance.updateOptions({ ...opt })
            maskInstance.resolve(field.value.toString());
            setFieldValue(field.name, maskInstance.value);
            opt.blocks.num.padFractionalZeros = false;
            maskInstance.updateOptions({ ...opt })
          } else {
            maskInstance.updateOptions({

              padFractionalZeros: mask.typeAux && mask.typeAux == 'integer' ? false : true
            })
            field.value  = mask.typeAux && mask.typeAux == 'integer' ? field.value.toString().replace('.', '')  : field.value;
            maskInstance.value  = mask.typeAux && mask.typeAux == 'integer' ? maskInstance.value.toString().replace('.', '')  : maskInstance.value;
            
            maskInstance.resolve(field.value.toString());
            setFieldValue(field.name, maskInstance.value);
            maskInstance.updateOptions({

              padFractionalZeros: false
            })
          }
        
        } else if (mask.type === 'monthYear') {
          let opt = mask;          
          opt.blocks.num.padFractionalZeros = false;
          maskInstance.updateOptions({ ...opt })
          maskInstance.resolve(field.value.toString());
          setFieldValue(field.name, maskInstance.value);
          opt.blocks.num.padFractionalZeros = false;
          maskInstance.updateOptions({ ...opt })

        } else {
          maskInstance.resolve(field.value);
          setFieldValue(field.name, maskInstance.value);
        }
      }
    } catch (e) {
      console.log('e', e)
    }
  }, []);

  const returnValue = () => {
    if (valueCalendar) {
      return format(valueCalendar)
    }

    if (field.value && (customValue === null || customValue === undefined) ) {
      // console.log("🚀 ~ returnValue ~ value:", field.value)
      // usando comparação de Null ou undefined para, caso o custom value esteja sendo passado, apesar de uma string vazia, seja priorizado
      return format(field.value)
    }

    if (customValue){
      return customValue
    }

    return ''
  }

  const returnPlaceholder = () => {
    const v = field.value
    const isEmpty =
      !v ||
      (typeof v === 'object' &&
        (!v.from || v.from === null) &&
        (!v.to || v.to === null))
  
    if ((placeholder && isEmpty) || isCalendar) return placeholder
    if (maskInstance && mask.mask === 'MM/YYYY' && isEmpty) return mask.mask
  
    return ''
  }
  

  return (
    <Wrapper
      inline={inline}
      disabled={disabled}
      inverse={inverseBlue}
      {...props}
    >
      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
        inline={inline}
        inverseBlue={inverseBlue}
      >
        {leftIcon && (
          leftIconText ? 
          <p style={{ margin: 0}}>{leftIconText}</p> :
          <IconButton onClick={leftIconClick}>
            <Icon name={leftIcon} />
          </IconButton>
      
        )}
        {leftRender && leftRender()}

        <Input
          ref={ref => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          size={size}
          type={type}
          placeholder={returnPlaceholder()}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          originalSize={originalSize}
          {...field}
          onKeyDown={e => {
            if (e.key.toLowerCase() === 'enter') {
              onPressEnter(field.value);
            }
          }}
          onFocus={(e) => {
            if (maskInstance) {
              if (mask.type == "currency" || mask.type == 'percentage') {
                if ('blocks' in mask) {
                  let opt = mask;
                  if(opt.blocks.num) {
                    opt.blocks.num.padFractionalZeros = false;
                  }
                  maskInstance.updateOptions({ ...opt })
                } else {
                  maskInstance.updateOptions({

                    padFractionalZeros: false
                  })
                }

              }
              if(mask.type === 'monthYear') {
                let opt = mask;
                maskInstance.updateOptions({ ...opt })
              }
            }
            handleScrollWhenInputIsFocused(e);
            onFocus(field.value);
            _onFocus();
          }}
          onBlur={(e) => {
            if (maskInstance) {
              if (mask.type == "currency" || mask.type == 'percentage') {
                const event = { ...e };
                if ('blocks' in mask) {
                  let opt = mask;
                  if(opt.blocks.num) {
                    opt.blocks.num.padFractionalZeros = false;
                  }
                  maskInstance.updateOptions({ ...opt })
                } else {
                  maskInstance.updateOptions({

                    padFractionalZeros: mask.typeAux && mask.typeAux == 'integer' ? false : true
                  })
                }
                field.value  = mask.typeAux && mask.typeAux == 'integer' ? field.value.toString().replace('.', '')  : field.value;
                maskInstance.value  = mask.typeAux && mask.typeAux == 'integer' ? maskInstance.value.toString().replace('.', '')  : maskInstance.value;
                maskInstance.resolve(e.target.value.toString());
                event.target.value = maskInstance.value;
                setFieldValue(field.name, maskInstance.value);

              }
              if(mask.type == 'monthYear') {
                const event = { ...e };
              }
            }
            onBlur(field.value);
            _onBlur();
            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          value={returnValue()}
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          onChange={e => {
            /*   let maskInstance = mask ? IMask(e.target,mask) : undefined; */
            let currentSelection = e.target.selectionStart;
            const event = { ...e };
            const origValue = e.target.value;
            if (maskInstance && mask.type == "currency" && origValue && origValue[origValue.length - 1] != ',' && origValue[origValue.length - 1] != '.') {
              if (mask.type == "currency" && origValue && (origValue[origValue.length - 1] == ',' || origValue[origValue.length - 1] == '.')) {
                event.target.value = parse(format(origValue));
                setFieldValue(field.name, parse(format(origValue)));
                onChange(event.target.value);
              } else {
                maskInstance.resolve(e.target.value);
                
                event.target.value = maskInstance.value;
                setFieldValue(field.name, mask.typeAux && mask.typeAux == 'integer'  ? maskInstance.value.toString().replace('.', '') : maskInstance.value);
                onChange( mask.typeAux && mask.typeAux == 'integer'  ? maskInstance.value.toString().replace('.', '') : maskInstance.value);
              }
              
              
            } else if (maskInstance && mask.type == "monthYear" && origValue && origValue[origValue.length - 1] != ',' && origValue[origValue.length - 1] != '.') {
              event.target.value = parse(format(origValue));
                setFieldValue(field.name, parse(format(origValue)));
                onChange(event.target.value);
            } else {
              event.target.value = parse(format(origValue));
              setFieldValue(field.name, parse(format(origValue)));
              onChange(event.target.value);
            }
             selectionStart.current = currentSelection;

          }}
          {...fieldProps}
        />
        {rightIcon && (
          <IconButton onClick={rightIconClick}>
            <Icon name={rightIcon} />
          </IconButton>
        )}
        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}
      </InputContainer>
      {validation && !inline && (
        <ValidationError
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default InputField;
